import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiChevronDown } from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReleaseInfo } from "../../../@types/services/ReleaseInfo";
import {
  fetchReleaseInfos,
  markNotYetSeenAsRead,
} from "../../../services/releaseInfos";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";

interface ReleaseInfoProps {
  releaseDate: string;
  translatedTitle: string;
  translatedDescription: string;
  seen: boolean;
}

const ReleaseInfoRow: React.FC<ReleaseInfoProps> = ({
  releaseDate,
  translatedTitle,
  translatedDescription,
  seen,
}) => {
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell
        sx={{
          borderLeftColor: seen ? "transparent" : "primary.main",
          borderLeftStyle: "solid",
          borderLeftWidth: "4px",
        }}
      >
        <Typography sx={{ fontWeight: "bold" }} variant="h6">
          {translatedTitle}
        </Typography>
        <Typography variant="body2">
          {t("intlDateTime", i18nextParseToDate(releaseDate))}
        </Typography>
        <Typography
          // biome-ignore lint/security/noDangerouslySetInnerHtml: The release content is sanitized in back-end admin and safe to use
          dangerouslySetInnerHTML={{
            __html: translatedDescription,
          }}
          variant="body1"
        />
      </TableCell>
    </TableRow>
  );
};

const MonthAccordion: React.FC<{
  month: string;
  releaseInfos: ReleaseInfo[];
  defaultExpanded?: boolean;
}> = ({ month, releaseInfos, defaultExpanded = false }) => {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<Icon icon={mdiChevronDown} />}>
        <Typography variant="h6">
          {month.charAt(0).toUpperCase() + month.slice(1).toLowerCase()}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer
          component={Paper}
          sx={{ border: "none", boxShadow: "none" }}
        >
          <Table>
            <TableBody>
              {releaseInfos.map((releaseInfo) => (
                <ReleaseInfoRow
                  key={releaseInfo.id}
                  releaseDate={releaseInfo.release_date}
                  seen={releaseInfo.seen}
                  translatedDescription={releaseInfo.translated_description}
                  translatedTitle={releaseInfo.translated_title}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

const YearAccordion: React.FC<{
  year: string;
  releaseInfos: ReleaseInfo[];
  defaultExpanded?: boolean;
}> = ({ year, releaseInfos, defaultExpanded = false }) => {
  const { t } = useTranslation();

  const [groupedReleaseInfos, setGroupedReleaseInfos] = useState<{
    [month: string]: ReleaseInfo[];
  }>({});

  useEffect(() => {
    setGroupedReleaseInfos(
      releaseInfos.reduce(
        (acc, info) => {
          const month = t(
            "intlDateLong",
            i18nextParseToDate(info.release_date, {
              val: {
                day: undefined,
                year: undefined,
              },
            }),
          );

          if (!acc[month]) acc[month] = [];
          acc[month].push(info);

          return acc;
        },
        {} as { [month: string]: ReleaseInfo[] },
      ),
    );
  }, [releaseInfos, t]);

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<Icon icon={mdiChevronDown} />}>
        <Typography style={{ fontWeight: "bold" }} variant="h5">
          {year}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {Object.keys(groupedReleaseInfos).map((month, index) => (
          <MonthAccordion
            defaultExpanded={defaultExpanded && index === 0}
            key={month}
            month={month}
            releaseInfos={groupedReleaseInfos[month] || []}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export const ReleaseInfos: React.FC<{ onlyNew?: boolean }> = ({
  onlyNew = false,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [releaseInfos, setReleaseInfos] = useState<ReleaseInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupedReleaseInfos, setGroupedReleaseInfos] = useState<{
    [year: string]: ReleaseInfo[];
  }>({});
  const [sortedYears, setSortedYears] = useState<string[]>([]);

  const loadReleaseInfos = useCallback(() => {
    setLoading(true);
    fetchReleaseInfos({
      filters: {
        seen: onlyNew ? false : undefined,
      },
    }).then((data) => {
      setReleaseInfos(data.results || []);
      setLoading(false);
    });
  }, [onlyNew]);

  const handleMarkNotYetSeenAsRead = useCallback(() => {
    markNotYetSeenAsRead().then(() => {
      loadReleaseInfos();
    });
  }, [loadReleaseInfos]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    handleMarkNotYetSeenAsRead();
  }, [handleMarkNotYetSeenAsRead]);

  useEffect(() => {
    loadReleaseInfos();
  }, [loadReleaseInfos]);

  useEffect(() => {
    if (releaseInfos.length === 0) return;

    setOpen(releaseInfos.some((info) => !info.seen));

    setGroupedReleaseInfos(
      releaseInfos.reduce(
        (acc, info) => {
          const date = new Date(info.release_date);
          const year = date.getFullYear().toString();

          if (!acc[year]) acc[year] = [];
          acc[year].push(info);

          return acc;
        },
        {} as { [year: string]: ReleaseInfo[] },
      ),
    );
  }, [releaseInfos]);

  useEffect(() => {
    if (releaseInfos.length === 0) return;

    setSortedYears(
      Object.keys(groupedReleaseInfos).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10),
      ),
    );
  }, [groupedReleaseInfos, releaseInfos.length]);

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        size="small"
        variant="outlined"
      >
        {t("topMenu.releaseInfos")}
      </Button>
      <Dialog fullWidth maxWidth="lg" onClose={handleClose} open={open}>
        <DialogTitle>{t("topMenu.releaseInfos")}</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            sortedYears.map((year, index) => (
              <YearAccordion
                defaultExpanded={index === 0}
                key={year}
                releaseInfos={groupedReleaseInfos[year] || []}
                year={year}
              />
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleClose}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
